import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { ScanProviderBinding } from 'src/app/common/ScanProviderBinding';
import {
  ScanRun,
  ScanRunStatusConstants,
  ScanRunStatusDisplayNames,
} from 'src/app/common/ScanRun';
import { CustomerService } from 'src/app/services/customer.service';
import { ScanProviderBindingService } from 'src/app/services/scan-provider-binding.service';
import { CallApiService } from '../../services/api-action.service';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

@Component({
  imports: [CommonModule, KENDO_GRID, GridNoRecordsComponent],
  selector: 'app-scan-provider-runs',
  templateUrl: './scan-provider-runs.component.html',
  styleUrls: ['./scan-provider-runs.component.css'],
})
export class ScanProviderRunsComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);
  private bindingService = inject(ScanProviderBindingService);
  private customerService = inject(CustomerService);
  private apiActionService = inject(CallApiService);

  public id: string;
  public binding: ScanProviderBinding;
  public customerName: string;
  public runs: ScanRun[];
  // Keeps the grid's "no records" template hidden and shows a spinner instead
  // while the runs are being fetched, so an in-flight load isn't mistaken for
  // "no runs". Starts true because runs are fetched on init.
  public loading: boolean = true;

  public ngOnInit() {
    this.id = this.activatedRoute.snapshot.paramMap.get('id');

    this.loading = true;
    this.apiActionService.callWithToasts(
      forkJoin({
        binding: this.bindingService.getById(this.id),
        customers: this.customerService.getAllForLookup(),
        runs: this.bindingService.getScanRuns(this.id),
      }).pipe(finalize(() => (this.loading = false))),
      null,
      $localize`Beim Laden der Scan-Läufe ist ein Fehler aufgetreten.`,
      ({ binding, customers, runs }) => {
        this.binding = binding;
        this.customerName =
          customers.find((c) => c.id === binding.customerId)?.fullName ?? binding.customerId;
        this.runs = this.toRuns(runs);
      }
    );
  }

  public refresh() {
    this.loading = true;
    this.apiActionService.callWithToasts(
      this.bindingService.getScanRuns(this.id).pipe(finalize(() => (this.loading = false))),
      null,
      $localize`Beim Laden der Scan-Läufe ist ein Fehler aufgetreten.`,
      (runs) => (this.runs = this.toRuns(runs))
    );
  }

  public statusName(status: number): string {
    return ScanRunStatusDisplayNames[status] ?? status?.toString();
  }

  public statusClass(status: number): string {
    switch (status) {
      case ScanRunStatusConstants.Succeeded:
        return 'bg-success';
      case ScanRunStatusConstants.Failed:
        return 'bg-danger';
      case ScanRunStatusConstants.Running:
        return 'bg-primary';
      case ScanRunStatusConstants.Cancelled:
        return 'bg-warning text-dark';
      default:
        return 'bg-secondary';
    }
  }

  public onBackClicked() {
    this.location.back();
  }

  private toRuns(runs: ScanRun[]): ScanRun[] {
    return runs.map((r) => ({
      ...r,
      startedAt: new Date(r.startedAt),
      finishedAt: r.finishedAt ? new Date(r.finishedAt) : null,
    }));
  }
}
